import axios from "axios";
import { MutableRefObject, useRef } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { signoutAsync } from "adminPanel/user/userSlice";
import IChildrenProps from "models/common-props/IChildrenProps";
import getOrganization from "helpers/getOrganization";
import { isReservedPathSegment } from "helpers/reservedPathSegments";
import { Snackbar } from "./Snackbar";
import reportHttpErrors from "helpers/reportHttpErrors";
import { isStoreDetailsIssueError } from "adminPanel/stores/isStoreDetailsIssueError";
import { InternalAxiosRequestConfig } from "axios";
import { clearKey, getOrCreateKey } from "helpers/idempotencyHelper";
import useTranslate from "hooks/useTranslate";
import { store } from "app/store";
import { handlePrintDesignMissingMandatoryError } from "adminPanel/Print/isPrintDesignMissingMandatoryError";
import usePrintDesignMissingMandatoryDialog from "adminPanel/Print/usePrintDesignMissingMandatoryDialog";

import { getSessionDeactivatedMessage } from "helpers/isSessionDeactivatedError";
// Extend Axios request config to include our custom properties
declare module "axios" {
  export interface AxiosRequestConfig {
    idempotent?: boolean;
    idempotencyGroup?: string;
  }
}

// Store the group in the request config for use in response interceptor
export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _idempotencyGroup?: string;
}
function reloadInterceptors(
  requestInterceptorIdRef: MutableRefObject<number | null>,
  responseInterceptorIdRef: MutableRefObject<number | null>,
  logOut: () => void,
  snack: typeof Snackbar,
  language: string,
  organizationName: string,
  translate?: any,
  onPrintDesignMissingMandatory?: () => void,
) {
  axios.interceptors.request.eject(requestInterceptorIdRef.current ?? -1);
  axios.interceptors.response.eject(responseInterceptorIdRef.current ?? -1);

  requestInterceptorIdRef.current = axios.interceptors.request.use(
    (config) => {
      const extendedConfig = config as ExtendedAxiosRequestConfig;
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      config.headers["time-zone"] = clientTimeZone;
      config.headers["X-Client-App"] = "admin";

      const headerAcceptLanguage = "Accept-Language";
      const pathname = window.location.pathname;

      const isPublicMenuRoute =
        pathname === "/public-menu" || pathname === "/public-menus";

      // قراءة Accept-Language المضبوطة مسبقاً على الطلب (تستخدمها صفحات
      // public-menu لاختيار لغة العرض بشكل مستقل عن لغة لوحة الإدارة).
      let presetAcceptLanguage = "";
      const headersForRead = config.headers;
      if (headersForRead) {
        if (typeof headersForRead.get === "function") {
          const v =
            headersForRead.get("Accept-Language") ??
            headersForRead.get("accept-language");
          presetAcceptLanguage = v != null && v !== "" ? String(v).trim() : "";
        } else {
          const raw = headersForRead as Record<string, unknown>;
          const v = raw["Accept-Language"] ?? raw["accept-language"];
          presetAcceptLanguage = v != null && v !== "" ? String(v).trim() : "";
        }
      }

      if (isPublicMenuRoute && presetAcceptLanguage) {
        config.headers[headerAcceptLanguage] = presetAcceptLanguage;
      } else if (language) {
        config.headers[headerAcceptLanguage] = language;
      } else {
        config.headers[headerAcceptLanguage] =
          localStorage.getItem("localLanguage");
      }

      const headerOrganizationName = "Organization-Name";
      const activeOrg =
        store.getState().user.organizationId || organizationName;

      const orgFromPublicQuery =
        isPublicMenuRoute &&
        new URLSearchParams(window.location.search).get("organization")?.trim();

      let presetOrgHeader = "";
      const hdrs = config.headers;
      if (hdrs) {
        if (typeof hdrs.get === "function") {
          const v =
            hdrs.get("Organization-Name") ?? hdrs.get("organization-name");
          presetOrgHeader = v != null && v !== "" ? String(v).trim() : "";
        } else {
          const raw = hdrs as Record<string, unknown>;
          const v = raw["Organization-Name"] ?? raw["organization-name"];
          presetOrgHeader = v != null && v !== "" ? String(v).trim() : "";
        }
      }

      const pathSegment = pathname.split("/").filter(Boolean)[0] ?? "";
      const orgFromPath = isReservedPathSegment(pathSegment) ? "" : pathSegment;

      // على صفحات القائمة العامة، معرّف المؤسسة في الرابط يجب أن يغلب جلسة المستخدم
      // وإلا يُرسل Organization-Name خاطئ ولا تُحمّل بيانات QR.
      if (isPublicMenuRoute && orgFromPublicQuery) {
        config.headers[headerOrganizationName] = orgFromPublicQuery;
      } else if (activeOrg && !isReservedPathSegment(activeOrg)) {
        config.headers[headerOrganizationName] = activeOrg;
      } else if (presetOrgHeader && !isReservedPathSegment(presetOrgHeader)) {
        config.headers[headerOrganizationName] = presetOrgHeader;
      } else if (orgFromPath) {
        config.headers[headerOrganizationName] = orgFromPath;
      } else {
        delete config.headers[headerOrganizationName];
      }

      if (config.idempotent && config.idempotencyGroup) {
        const idempotencyKey = getOrCreateKey(config.idempotencyGroup);

        config.headers["Idempotency-Key"] = idempotencyKey;

        extendedConfig._idempotencyGroup = config.idempotencyGroup;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  responseInterceptorIdRef.current = axios.interceptors.response.use(
    (response) => {
      const config = response.config as ExtendedAxiosRequestConfig;

      if (config._idempotencyGroup) {
        clearKey(config._idempotencyGroup);
      }

      return response;
    },
    (error) => {
      const config = error.config as ExtendedAxiosRequestConfig | undefined;
      if (config?._idempotencyGroup) {
        clearKey(config._idempotencyGroup);
      }

      // Sign-out failures must not show a snack; local cleanup still runs in signoutAsync.
      if (config?.url?.includes("users/sign-out")) {
        return Promise.reject(error);
      }

      if (
        error.code === "ERR_NETWORK" &&
        config?.url?.includes("/api/printing")
      ) {
        snack(translate("couldntReachPrinter"), "error");
        return Promise.reject(error);
      }

      const status = error.response?.status;

      // Check if session was deactivated (e.g. logged in on another device
      // with "close other sessions"). Show a single yellow warning and force
      // logout. Callers must NOT show any additional red toast for this case.
      const sessionDeactivatedMessage = getSessionDeactivatedMessage(error);

      if (sessionDeactivatedMessage) {
        // Session was deactivated due to permission changes, force logout
        // Show message first, then logout after delay so user can see the message
        snack(sessionDeactivatedMessage || "Session expired", "warning");
        if (!(error as any).__sessionDeactivatedHandled) {
          (error as any).__sessionDeactivatedHandled = true;
          setTimeout(() => {
            logOut();
          }, 3000);
        }
        return Promise.reject(error);
      }

      if (
        handlePrintDesignMissingMandatoryError(
          error.response,
          config?.url,
          () => onPrintDesignMissingMandatory?.(),
        )
      ) {
        return Promise.reject(error);
      }

      if (
        status === 403 ||
        status === 409 ||
        status === 406 ||
        status === 432 ||
        status === 488 || //EGPTaxPosNotLinkedToDeviceException
        status === 489 || //SYRTaxStoreNotLinkedToStationException
        status === 477 || //BusinessInfoRequiredException
        status === 478 // EmployeeNotActiveException
      ) {
        return Promise.reject(error);
      }
      if (isStoreDetailsIssueError(error.response)) {
        return Promise.reject(error);
      }
      reportHttpErrors(error.response, snack);
      return Promise.reject(error);
    },
  );
}

export default function HttpInterceptor({ children }: IChildrenProps) {
  const dispatch = useAppDispatch();
  const requestInterceptorIdRef = useRef<number | null>(null);
  const responseInterceptorIdRef = useRef<number | null>(null);
  const { translate } = useTranslate("shared.error");
  const empLang = useAppSelector((s) => s.user.preferedLanguage);
  const organizationId = useAppSelector((s) => s.user.organizationId);
  const currentLanguage = localStorage.getItem("localLanguage");
  const languageChanged = currentLanguage !== empLang;
  const orgIdRef = useRef(organizationId);
  const organizationChanged = orgIdRef.current !== organizationId;
  const showPrintDesignMissingMandatory =
    usePrintDesignMissingMandatoryDialog();

  if (
    !requestInterceptorIdRef.current ||
    !responseInterceptorIdRef.current ||
    languageChanged ||
    organizationChanged
  ) {
    orgIdRef.current = organizationId;
    reloadInterceptors(
      requestInterceptorIdRef,
      responseInterceptorIdRef,
      () => dispatch(signoutAsync()),
      Snackbar,
      empLang,
      organizationId || getOrganization(),
      translate,
      showPrintDesignMissingMandatory,
    );
  }

  return children;
}
